// Premium Today screen — pure presentation model (2026-10-23). Everything here only READS data the existing APIs
// already return (/weekly-plan/all-active, /garmin/activities, /garmin/activities/{id}/splits, /insights/workout-trend)
// and arranges it for display. No training metric is computed, nothing is written, and a value the API did not
// supply is never shown — it is left out, never rendered as 0 or "—".

export const HE_LETTERS = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'];
export const HE_DAYS = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
const HE_MONTHS = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר',
  'נובמבר', 'דצמבר'];

const pad = (n) => String(n).padStart(2, '0');

/** Local calendar date 'YYYY-MM-DD' (no UTC drift). */
export function isoOf(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function dateOf(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

/** 'שישי, 23 באוקטובר' */
export function headerDate(today) {
  return `${HE_DAYS[today.getDay()]}, ${today.getDate()} ב${HE_MONTHS[today.getMonth()]}`;
}

/** 'D.M' — RTL-safe (never inside parentheses). */
export function dayMonth(iso) {
  const d = dateOf(iso);
  return d ? `${d.getDate()}.${d.getMonth() + 1}` : '';
}

/** The plan week (Sunday..Saturday) that contains `today`. */
export function weekDays(today) {
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay());
  return HE_LETTERS.map((letter, i) => {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    return { iso: isoOf(d), letter, dayNum: d.getDate(), name: HE_DAYS[i] };
  });
}

/** Week range label '18–24.10' (or '28.9–4.10' across months). */
export function weekRangeLabel(days) {
  const a = dateOf(days[0].iso);
  const b = dateOf(days[days.length - 1].iso);
  if (!a || !b) return '';
  return a.getMonth() === b.getMonth()
    ? `${a.getDate()}–${b.getDate()}.${b.getMonth() + 1}`
    : `${a.getDate()}.${a.getMonth() + 1}–${b.getDate()}.${b.getMonth() + 1}`;
}

function parseWorkouts(p) {
  let w = p && (p.workouts_json ?? p.plan_json);
  if (typeof w === 'string') {
    try { w = JSON.parse(w); } catch { w = null; }
  }
  return w && typeof w === 'object' ? w : null;
}

/** { 'YYYY-MM-DD': planDay } from /weekly-plan/all-active — RUNNING plans only (cycling is hidden from the UI). */
export function planDaysByDate(payload) {
  const plans = Array.isArray(payload) ? payload : (payload && Array.isArray(payload.plans) ? payload.plans : []);
  const out = {};
  for (const p of plans) {
    if (p && p.sport_type && p.sport_type !== 'running') continue;
    const w = parseWorkouts(p);
    const days = (w && Array.isArray(w.days)) ? w.days : [];
    const ws = dateOf(p.week_start_date || (w && w.week_start));
    days.forEach((d, i) => {
      if (!d || typeof d !== 'object') return;
      let iso = String(d.date || '').slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(iso) && ws) {
        const idx = HE_DAYS.indexOf(String(d.day_name || '').trim());
        const off = idx >= 0 ? idx : i;
        iso = isoOf(new Date(ws.getFullYear(), ws.getMonth(), ws.getDate() + off));
      }
      if (/^\d{4}-\d{2}-\d{2}$/.test(iso) && !out[iso]) out[iso] = d;
    });
  }
  return out;
}

const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const isRun = (a) => !a.sport_type || a.sport_type === 'running' || a.activity_type === 'Run';

/** The day's completed runs (longest first). */
export function runsOn(activities, iso) {
  return (activities || [])
    .filter((a) => a && isRun(a) && String(a.date || '').slice(0, 10) === iso && (num(a.distance) || 0) > 0)
    .sort((x, y) => (num(y.distance) || 0) - (num(x.distance) || 0));
}

/** A completed workout known only from the coach card (no device feed — e.g. no Garmin): its measured facts. */
export function trendActivity(trend, iso) {
  const tw = trend && trend.today_workout;
  if (!tw || String(tw.date || '').slice(0, 10) !== iso || !(num(tw.distance) > 0)) return null;
  return { source: null, distance: tw.distance, averagePace: tw.pace || null,
    averageHR: tw.hr_kind === 'activity' || !tw.hr_kind ? num(tw.hr) : null, fromCard: true };
}

/** 'completed' | 'planned' | 'missed' | 'rest' | 'none' for one day. */
export function dayStatus(iso, todayIso, hasRun, planDay) {
  if (hasRun) return 'completed';
  if (planDay && planDay.is_rest) return 'rest';
  if (planDay) return iso < todayIso ? 'missed' : 'planned';
  return 'none';
}

/** '46:25' / '1:02:15' from seconds; null when unknown. */
export function formatDuration(sec) {
  const s = num(sec);
  if (!s || s <= 0) return null;
  const t = Math.round(s);
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const r = t % 60;
  return h ? `${h}:${pad(m)}:${pad(r)}` : `${m}:${pad(r)}`;
}

export function paceOf(sec) {
  const s = num(sec);
  if (!s || s <= 0) return null;
  const t = Math.round(s);
  return `${Math.floor(t / 60)}:${pad(t % 60)}`;
}

/** 'HH:MM' start time from startTimeLocal, else null. */
export function startTime(act) {
  const m = /T(\d{2}):(\d{2})/.exec(String((act && act.startTimeLocal) || ''));
  return m ? `${m[1]}:${m[2]}` : null;
}

/** The secondary metric tiles — only the ones the API supplied (a 0 / null heart rate or climb is left out). */
export function metricTiles(act) {
  if (!act) return [];
  const t = [];
  const hr = num(act.averageHR);
  const mx = num(act.maxHR);
  const cad = num(act.averageCadence);
  const el = num(act.elevationGain);
  if (hr && hr > 0) t.push({ key: 'avg_hr', value: String(Math.round(hr)), label: 'דופק ממוצע' });
  if (mx && mx > 0) t.push({ key: 'max_hr', value: String(Math.round(mx)), label: 'דופק מקס׳' });
  if (cad && cad > 0) t.push({ key: 'cadence', value: String(Math.round(cad)), label: 'קדנס' });
  if (el && el > 0) t.push({ key: 'elev', value: `${Math.round(el)} מ׳`, label: 'טיפוס' });
  return t;
}

/** Garmin's Aerobic Training Effect as the API sent it (0.0-5.0 scale), for the ring — null when the watch did not
 *  record it (a 0.0 / out-of-scale value is not drawn). */
export function aerobicTE(act) {
  const v = num(act && act.aerobicTrainingEffect);
  return v !== null && v > 0 && v <= 5 ? Math.round(v * 10) / 10 : null;
}

/** Per-km pace bars from the measured splits: full kilometres only (a GPS remainder is not a kilometre).
 *  Faster = taller; the fastest is marked. null with fewer than 2 full splits. */
export function paceBars(splits) {
  const full = (splits || []).filter((s) => s && !s.partial && (num(s.pace_sec) || 0) > 0);
  if (full.length < 2) return null;
  const secs = full.map((s) => s.pace_sec);
  const fast = Math.min(...secs);
  const slow = Math.max(...secs);
  const span = slow - fast;
  const fastestIdx = secs.indexOf(fast);
  return {
    bars: full.map((s, i) => ({
      key: s.split ?? i + 1,
      pace: paceOf(s.pace_sec),
      // 35%..100% of the chart height, linear in speed (equal splits -> all full height)
      rel: span > 0 ? 0.35 + 0.65 * ((slow - s.pace_sec) / span) : 1,
      fastest: i === fastestIdx && span > 0,
    })),
    range: `${paceOf(fast)} – ${paceOf(slow)}`,
  };
}

/** Planned pace '4:25–4:35' (a cycling / effort-only day has '0:00' -> none). */
export function plannedPace(day) {
  const p = day && day.pace;
  if (!p || typeof p !== 'object') return null;
  const ok = (v) => typeof v === 'string' && /^\d{1,2}:\d{2}$/.test(v) && v !== '0:00';
  if (ok(p.min) && ok(p.max)) return p.min === p.max ? p.min : `${p.min}–${p.max}`;
  return ok(p.min) ? p.min : (ok(p.max) ? p.max : null);
}

/** The structured main set of a planned day, e.g. "5 × 1000 מ'", when the plan names one. */
export function mainSet(day) {
  const segs = (day && Array.isArray(day.structure)) ? day.structure : [];
  const s = segs.find((x) => x && /\d\s*[×x]\s*\d/.test(String(x.segment || '')));
  return s ? String(s.segment).trim() : null;
}

const ZONE_CHIP = { VO2max: 'VO2 Max', Threshold: 'סף', Intervals: 'אינטרוולים', 'Race Pace': 'קצב מרוץ' };
/** The planned intensity chip — quality zones only (an easy day carries no chip). */
export function zoneChip(day) {
  return (day && ZONE_CHIP[String(day.target_zone || '').trim()]) || null;
}

const r1 = (v) => Math.round(v * 10) / 10;

/** This week's summary. basis 'completed' when there is a completed-activity feed, else 'planned' (the plan's own
 *  kilometres, labelled as planned), else null (no card). */
export function weekSummary(days, activities, planByIso, todayIso, hasFeed) {
  const planned = days.map((d) => {
    const p = planByIso[d.iso];
    return p && !p.is_rest ? (num(p.km) || 0) : 0;
  });
  const plannedKm = r1(planned.reduce((a, b) => a + b, 0));
  if (hasFeed) {
    const per = days.map((d) => runsOn(activities, d.iso));
    const km = per.map((list) => r1(list.reduce((a, x) => a + (num(x.distance) || 0), 0)));
    const total = r1(km.reduce((a, b) => a + b, 0));
    const count = per.reduce((a, list) => a + list.length, 0);
    const runDays = per.filter((list) => list.length > 0).length;
    const max = Math.max(...km, 0);
    return {
      basis: 'completed', km: total, count, runDays, plannedKm: plannedKm || null,
      bars: days.map((d, i) => ({ iso: d.iso, letter: d.letter, km: km[i], rel: max ? km[i] / max : 0,
        isToday: d.iso === todayIso, future: d.iso > todayIso })),
    };
  }
  if (!plannedKm) return null;
  const max = Math.max(...planned, 0);
  return {
    basis: 'planned', km: plannedKm, count: null, runDays: planned.filter((k) => k > 0).length, plannedKm,
    bars: days.map((d, i) => ({ iso: d.iso, letter: d.letter, km: planned[i], rel: max ? planned[i] / max : 0,
      isToday: d.iso === todayIso, future: d.iso > todayIso, rest: !!(planByIso[d.iso] && planByIso[d.iso].is_rest) })),
  };
}

/** The next planned workout line from the coach card's next_workout (+ the plan day's own main set when known). */
export function nextWorkoutView(next, planByIso, todayIso) {
  if (!next) return null;
  const iso = String(next.date || '').slice(0, 10);
  const d = dateOf(iso);
  if (d && iso <= todayIso) return null;          // today is the main card; never "next" for a past day
  const pd = iso ? planByIso[iso] : null;
  const km = num(next.planned_distance) || num(pd && pd.km);
  const title = [next.label || (pd && pd.type) || 'אימון', km ? `${km} ק״מ` : null].filter(Boolean).join(' · ');
  const set = mainSet(pd);
  const pace = next.planned_pace || plannedPace(pd) || null;
  return {
    title,
    sub: [set, pace].filter(Boolean).join(' · ') || null,
    set,
    pace,
    dayName: d ? HE_DAYS[d.getDay()] : (next.day_name || null),
    dayNum: d ? d.getDate() : null,
  };
}
/** Is there any running plan week at all (the "no plan yet" state)? */
export function hasRunningPlan(payload) {
  return Object.keys(planDaysByDate(payload)).length > 0;
}

/** Screen-reader text for the per-km pace chart (the bars themselves are decorative). */
export function paceChartLabel(data) {
  if (!data) return '';
  return `קצב לפי קילומטר: ${data.bars.map((b) => `ק״מ ${b.key} ${b.pace}`).join(', ')}`;
}

/** Screen-reader text for the week bars. */
export function weekChartLabel(summary) {
  if (!summary) return '';
  const kind = summary.basis === 'planned' ? 'מתוכנן' : 'בוצע';
  return `קילומטרים לפי יום, ${kind}: ${summary.bars.map((b) => `${b.letter} ${b.km}`).join(', ')}`;
}

/** Tapped pace bar: "ק״מ 4 · 4:52 דק׳/ק״מ" — the measured split only. */
export function paceBarLabel(bar) {
  return bar ? `ק״מ ${bar.key} · ${bar.pace} דק׳/ק״מ` : '';
}

/** Tapped week bar: "שבת 24.10 · 12.0 ק״מ". A 0 is shown only where the data supports it: a past / today day of the
 *  completed-activity feed with no run recorded. A later day has no data yet; the planned week says what is planned. */
export function weekBarLabel(bar, basis) {
  if (!bar) return '';
  const d = dateOf(bar.iso);
  const day = d ? `${HE_DAYS[d.getDay()]} ${d.getDate()}.${d.getMonth() + 1}` : bar.letter;
  if (basis === 'planned') {
    if (bar.km > 0) return `${day} · ${bar.km} ק״מ מתוכננים`;
    return `${day} · ${bar.rest ? 'מנוחה' : 'אין אימון מתוכנן'}`;
  }
  if (bar.km > 0) return `${day} · ${bar.km.toFixed(1)} ק״מ`;
  return bar.future ? `${day} · עדיין אין נתונים` : `${day} · 0 ק״מ`;
}
